import once from 'lodash/once';
import debounce from 'lodash/debounce';

const config = {
  attributes: true,
};
/**
 * Generic number input component that stores internally a float value of an integer,
 * but externally displays a formatted string per locale settings. Binds into a stage
 * component, works autonomously and invisbly behind the scenes. Only downside is that
 * input.value = '123' doesn't trigger render. Instead input.write(123) needs to be called.
 * Only workaround would be to use polling, because directly writing to value doesn't trigger
 * any event that is observable.
 *
 * TO-DO: Test browser compatibility. Only Mac Chrome tested.
 *
 * Please avoid jQuery here if possible. Vanilla JS does wonders.
 *
 * @type {Object}
 * @author KL
 */
const numberField = {
  /**
   * Init engine. Wrapped in lodash once to make sure it's not called repeatedly
   */
  init: once(() => {
    numberField.observer = new MutationObserver(numberField.observerCallback);
    // Find all the input elements from a page and bind the additional functionality to those
    document.querySelectorAll('.input-number').forEach(input => {
      input.precision = parseInt(input.getAttribute('data-precision')) || 0;
      input.formatter = new Intl.NumberFormat('fi-FI', {
        style: 'decimal',
        minimumFractionDigits: input.precision,
        maximumFractionDigits: input.precision,
      });
      input.read = numberField.read;
      input.write = numberField.write;
      input.render = numberField.render;
      input.round = numberField.round;
      input.addEventListener('change', numberField.userEnter);
      input.addEventListener('input', numberField.userEnter);
      input.display = document.getElementById(input.getAttribute('id') + '-display');
      const inputStyle = window.getComputedStyle(input);

      input.display.style.paddingTop = inputStyle['padding-top'];
      input.display.style.paddingLeft = inputStyle['padding-left'];
      input.display.style.paddingBottom = inputStyle['padding-bottom'];
      input.display.style.fontSize = inputStyle['font-size'];

      input.write(input.value, input.dataset.min, input.dataset.max);

      input.render(input.read());

      input.placeholder = input.formatter.format(input.placeholder);
      numberField.observer.observe(input, config);
    });
  }),

  observer: null,

  /**
   * Callback handler for mutationObserver
   * @param  {object} mutationRecord https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   * @param  {object} observer       https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   */
  observerCallback: function(mutationRecord, observer) {
    for (const event of mutationRecord) {
      if (event.attributeName === 'data-value') {
        event.target.render(event.target.read());
      }
    }
  },

  /**
   * Returns the internal value
   * @return {float} internal value
   */
  read: function() {
    let value = parseFloat(this.getAttribute('data-value'));
    value = !isNaN(value) ? value : 0;
    return value;
  },

  /**
   * Validates and updates given input to internal value
   * @param  {float} value input
   * @param  {float} min minimum allowed value
   * @param  {float} max maximum allowed value
   * @return {float}       parsed internal value
   */
  write: function(value, min, max) {
    let timeout;

    if (!timeout) {
      timeout = setTimeout(function() {
        timeout = null;
        if (max && +value > +max) {
          value = max;
        }
      }, 500);
    }

    if (!timeout) {
      timeout = setTimeout(function() {
        timeout = null;
        if (min && +value < +min) {
          value = min;
        }
      }, 500);
    }

    value = parseFloat(value);
    value = !isNaN(value) ? this.round(value) : 0;
    this.setAttribute('data-value', value);
    this.value = value;
    return value;
  },

  /**
   * Renders the external value display
   * @param  {float} value numeric value
   */
  render: function(value) {
    this.display.innerHTML = this.formatter.format(value);
  },

  userEnter: debounce(
    /**
     * captures user triggered onchange event, update the internal value an renders the external value view
     * @param  {event} event native input onchange event
     */
    function(event) {
      // eslint-disable-next-line no-useless-escape
      this.render(this.write(this.value.replace(/[^\d,.]/, '').replace(/\,/, '.'), this.dataset.min, this.dataset.max));
    },
    500,
    { leading: true, trailing: true },
  ),

  /**
   * [round description]
   * @param  {float} value numbervalue
   * @return {float} rounded to precision
   */
  round: function(value) {
    return Math.round(value * Math.pow(10, this.precision)) / Math.pow(10, this.precision);
  },
};

export default numberField;
